import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

class GiftWithPurchaseHandler extends EventTarget {
  constructor() {
    super();

    this.isBound = false;
    this.changeBound = false;

    this.reInit = this.reInit.bind(this);

    this.init();
    this.bindModalCloseStorage();
    document.addEventListener(ThemeEvents.cartUpdate, this.reInit);
  }

  /* ------------------------------------------
   Session helpers
  ------------------------------------------ */

  getDiscountKey() {
    const btn = document.querySelector('.gift-with-purchase-modal-btn');
    return btn?.dataset.discount || 'default';
  }

  getSessionKey() {
    return `has_seen_gwp_${this.getDiscountKey()}`;
  }

  hasUserSeenModal() {
    return sessionStorage.getItem(this.getSessionKey()) === 'true';
  }

  markModalSeen() {
    sessionStorage.setItem(this.getSessionKey(), 'true');
  }

  shouldOpenModal() {
    return !this.hasUserSeenModal();
  }

  bindModalCloseStorage() {
    const modalClose = document.querySelector('.gift-with-purchase-modal__close');
    if (!modalClose) return;

    modalClose.addEventListener('click', () => {
      this.markModalSeen();
    });
  }

  /* ------------------------------------------
   Initial Cart Check
  ------------------------------------------ */

  async checkInitialCart() {
    const cfg = window.gwpCampaign;
    const button = document.querySelector('.gift-with-purchase-modal-btn');

    if (!cfg || !button) return;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      const cartItems = cart.items;
      const cartSubTotal = cart.items_subtotal_price / 100;

      let gwpEligible = false;
      let gwpProduct = null;

      if (cartItems.length == 0) {
        return;
      }

      // detect existing GWP
      cartItems.forEach((item) => {
        if (item.properties && item.properties._is_gwp) {
          gwpProduct = item;
        }
      });

      // eligibility: subtotal OR product
      cfg.forEach((rule) => {
        const triggerId = rule.productTrigger?.id;
        const subtotalMatch = rule.subtotalTrigger != null && rule.subtotalTrigger <= cartSubTotal;
        const productMatch = triggerId && cartItems.some((item) => item.product_id == triggerId);
      
        if (subtotalMatch || productMatch) {
          gwpEligible = true;
        }
      });

      // ❗ Not eligible → remove gift & reset session
      if (!gwpEligible && gwpProduct) {
        sessionStorage.removeItem(this.getSessionKey());
        await this.removeExistingGwp();
      }

      // ✅ Eligible → open modal if allowed
      else if (gwpEligible && !gwpProduct && this.shouldOpenModal()) {
        this.openModal();
      }

    } catch (err) {
      console.error('Initial cart check failed:', err);
    }
  }

  /* ------------------------------------------
   Cart Update → Eligibility check
  ------------------------------------------ */

  async reInit() {
    const cfg = window.gwpCampaign;
    const button = document.querySelector('.gift-with-purchase-modal-btn');
  
    if (!cfg || !button) return;
  
    const sectionId = button.dataset.sectionId;
    if (!sectionId) return;
  
    try {
      // Fetch fresh section HTML
      const res = await fetch(`/?sections=${sectionId}`);
      const data = await res.json();
  
      const html = data[sectionId];
      if (!html) return;
  
      const parsed = new DOMParser().parseFromString(html, 'text/html');
  
      /* ------------------------------------------
        🔄 Update ONLY button attributes
      ------------------------------------------ */
      const newBtn = parsed.querySelector('.gift-with-purchase-modal-btn');

      if (newBtn && button) {
        Array.from(newBtn.attributes).forEach(attr => {
          button.setAttribute(attr.name, attr.value);
        });
      }
  
      /* ------------------------------------------
        🔄 Update ONLY modal content
        (inside dialog-modal)
      ------------------------------------------ */
      const newModalBody = parsed.querySelector('.gift-with-purchase-modal__body');
      const currentModalBody = document.querySelector('.gift-with-purchase-modal__body');

      if (newModalBody && currentModalBody) {
        currentModalBody.innerHTML = newModalBody.innerHTML;
      }

      // Also update the modal title if it changed
      const newModalTitle = parsed.querySelector('.gift-with-purchase-modal__title');
      const currentModalTitle = document.querySelector('.gift-with-purchase-modal__title');

      if (newModalTitle && currentModalTitle) {
        currentModalTitle.textContent = newModalTitle.textContent;
      }

      // Update product count attribute on dialog
      const newDialog = parsed.querySelector('.gift-with-purchase-modal__content');
      const currentDialog = document.querySelector('.gift-with-purchase-modal__content');

      if (newDialog && currentDialog) {
        const productCount = newDialog.getAttribute('data-product-count');
        if (productCount) {
          currentDialog.setAttribute('data-product-count', productCount);
        }
      }
  
      /* ------------------------------------------
        Now re-query updated elements
      ------------------------------------------ */
      const updatedBtn = document.querySelector('.gift-with-purchase-modal-btn');
  
      let cartSubTotal = updatedBtn?.dataset.cartSubtotal;
      if (!cartSubTotal) return;
  
      cartSubTotal = parseInt(cartSubTotal) / 100;
  
      const cartItems = parsed.querySelectorAll('.cart-items__wrapper .cart-items__table-row');
      const gwpProduct = parsed.querySelector('.gwp-cart-item');
  
      let gwpEligible = false;
  
      cfg.forEach((rule) => {
        const triggerId = rule.productTrigger?.id;
        const subtotalMatch = rule.subtotalTrigger != null && rule.subtotalTrigger <= cartSubTotal;
        const productMatch = triggerId && Array.from(cartItems).some((item) => item.dataset.productId == triggerId);
      
        if (subtotalMatch || productMatch) {
          gwpEligible = true;
        }
      });
  
      /* ------------------------------------------
        Eligibility handling
      ------------------------------------------ */
  
      if (!gwpEligible && gwpProduct) {
        sessionStorage.removeItem(this.getSessionKey());
        await this.removeExistingGwp(true);
      } else if (gwpEligible && !gwpProduct && this.shouldOpenModal()) {
        this.openModal();
      }
  
      /* ------------------------------------------
         Re-bind events (important!)
      ------------------------------------------ */
  
      this.isBound = false;
      this.changeBound = false;
  
      this.bindAddToCart();
      this.bindChangeGwp();
      this.bindModalCloseStorage();
  
    } catch (err) {
      console.error('reInit fetch failed:', err);
    }
  }
  /* ------------------------------------------
   Helpers
  ------------------------------------------ */

  getSections(formData) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }
  }

  async removeExistingGwp(useDomClick = false) {
    const gwpItem = document.querySelector('.gwp-cart-item');
    if (!gwpItem) return;

    if (useDomClick) {
      const removeBtn = gwpItem.querySelector('.cart-remove-button');
      if (!removeBtn) return;

      await new Promise((resolve) => {
        const handleCartUpdate = () => {
          document.removeEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
          resolve();
        };

        document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate, { once: true });
        removeBtn.click();
      });
    } else {
      const key = gwpItem.dataset.key;
      if (!key) return;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    }
  }

  openModal() {
    if (!this.shouldOpenModal()) return;
    this.markModalSeen();

    document.querySelector('.gift-with-purchase-modal-btn-open-js')?.click();
  }

  /* ------------------------------------------
   GWP Add to cart
  ------------------------------------------ */

  bindAddToCart() {
    if (this.isBound) return;

    const modal = document.querySelector('.gift-with-purchase-modal');
    if (!modal) return;

    const productAddBtns = modal.querySelectorAll('.gift-product-card__button');
    if (!productAddBtns.length) return;

    this.isBound = true;

    productAddBtns.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        const that = e.currentTarget;

        const variantId = that.dataset.variantId;
        if (!variantId) return;

        that.setAttribute('disabled', 'true');

        await this.removeExistingGwp();

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        formData.append('properties[_is_gwp]', 'true');

        this.getSections(formData);

        const fetchCfg = fetchConfig('javascript', { body: formData });

        fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            document.querySelector('.gift-with-purchase-modal__close')?.click();

            const sectionId = document.querySelector('cart-items-component')?.dataset.sectionId;

            const newSectionHTML = new DOMParser().parseFromString(
              data.sections[sectionId],
              'text/html'
            );

            const newCartHiddenItemCount =
              newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;

            const newCartItemCount = newCartHiddenItemCount
              ? parseInt(newCartHiddenItemCount, 10)
              : 0;

            document.dispatchEvent(
              new CartUpdateEvent({}, sectionId, {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: data.sections,
              })
            );

            morphSection(sectionId, data.sections[sectionId]);

            this.applyDiscount(that);
          })
          .catch((error) => console.error('GWP add error:', error))
          .finally(() => {
            that.removeAttribute('disabled');
          });
      });
    });
  }

  applyDiscount(that) {
    const discount = that.dataset.discount;
    if (!discount) return;

    const discountInput = document.querySelector('.cart-discount__input');
    const discountButton = document.querySelector('.cart-discount__button');

    if (!discountInput || !discountButton) return;

    discountInput.value = discount;
    discountButton.click();
  }

  /* ------------------------------------------
   Change GWP
  ------------------------------------------ */

  bindChangeGwp() {
    if (this.changeBound) return;
    this.changeBound = true;

    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target.classList.contains('gwp-product-change')) {
        document.querySelector('.gift-with-purchase-modal-btn')?.click();
      }
    });
  }

  /* ------------------------------------------
   Init
  ------------------------------------------ */

  init() {
    this.checkInitialCart();
    this.bindAddToCart();
    this.bindChangeGwp();
  }
}

// Initialize
new GiftWithPurchaseHandler();